/**
 * Entrada no sistema — usuário e senha.
 *
 * O sistema atual não tem tela de login: quem alcança o IP na LAN tem acesso
 * a tudo, inclusive ao caixa. Aqui a conta vive em `auth_usuarios`, no
 * Postgres, e quem confere a senha é o próprio banco.
 *
 * A senha existe só dentro do estado deste componente até virar corpo do
 * POST. O token da sessão volta em cookie httpOnly — nada em localStorage,
 * nada que o JavaScript da página consiga ler.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useEntrar, useSessao, useTrocarSenha } from '../features/queries';
import { ApiError } from '../lib/api';
import { Botao, Campo, Faixa, Input, Spinner } from '../components/ui';

export function Login() {
  const navegar = useNavigate();
  const sessao = useSessao();
  const entrar = useEntrar();

  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  /**
   * Falha que acontece DEPOIS de um POST bem-sucedido — quando o servidor
   * respondeu 200 mas a sessão não existe. Não é `entrar.error`, que só
   * cobre resposta de erro.
   */
  const [falha, setFalha] = useState<string | null>(null);

  if (sessao.isLoading) {
    return (
      <Moldura>
        <div className="flex justify-center py-6 text-slate-400">
          <Spinner className="h-6 w-6" />
        </div>
      </Moldura>
    );
  }

  // Já logado, mas com senha provisória: a troca vem antes de qualquer tela.
  if (sessao.data?.usuario?.deveTrocarSenha) {
    return <TrocaObrigatoria nome={sessao.data.usuario.nome} />;
  }

  function submeter(evento: FormEvent): void {
    evento.preventDefault();
    setFalha(null);

    entrar.mutate(
      { usuario: usuario.trim().toLowerCase(), senha },
      {
        onSuccess: async (resposta) => {
          setSenha('');

          // 200 sem usuário no corpo: API mais antiga (ou outra API) atendendo
          // nesta URL. Antes isto explodia num `Cannot read properties of
          // undefined` e a tela ficava parada, sem dizer nada a quem tentou.
          if (!resposta?.usuario) {
            setFalha(
              'O servidor respondeu, mas não devolveu a sessão. A API pode estar numa versão antiga — avise o suporte técnico.',
            );
            return;
          }

          // Confirma a sessão em vez de confiar no POST: o cookie é httpOnly,
          // então a única forma de saber que ele COLOU é perguntar quem sou eu.
          // É aqui que aparece o caso do navegador que descarta o cookie por
          // ser de outro domínio — antes disto, a tela só girava e voltava.
          const { data } = await sessao.refetch();

          if (!data?.usuario) {
            setFalha(
              'Usuário e senha corretos, mas o navegador não guardou a sessão. ' +
                'Costuma ser bloqueio de cookie entre domínios diferentes — avise o suporte técnico.',
            );
            return;
          }

          // Senha provisória não dá acesso ao sistema: a própria tela de
          // login vira a tela de troca (a `Portaria` faz o mesmo nas demais).
          if (!data.usuario.deveTrocarSenha) void navegar({ to: '/' });
        },
      },
    );
  }

  return (
    <Moldura>
      <form onSubmit={submeter} className="flex flex-col gap-4">
        <Campo rotulo="Usuário" obrigatorio>
          {(props) => (
            <Input
              {...props}
              autoFocus
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
            />
          )}
        </Campo>

        <Campo rotulo="Senha" obrigatorio>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="current-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
          )}
        </Campo>

        {entrar.error && <ErroDeLogin erro={entrar.error} />}
        {falha && <Faixa tipo="erro">{falha}</Faixa>}

        <Botao
          type="submit"
          carregando={entrar.isPending}
          textoCarregando="Entrando…"
          disabled={usuario.trim().length < 3 || senha.length < 8}
          className="mt-1 w-full"
        >
          Entrar
        </Botao>

        <p className="text-center text-xs text-slate-400">
          Esqueceu a senha? Peça a redefinição a quem administra o sistema.
        </p>
      </form>
    </Moldura>
  );
}

/**
 * Troca obrigatória da senha provisória.
 *
 * Conta criada por outra pessoa nasce com `deve_trocar_senha = true` no
 * banco — inclusive a `admin` da semente, cuja senha está escrita num
 * arquivo versionado. Enquanto a troca não acontece, nenhuma tela abre.
 */
export function TrocaObrigatoria({ nome }: { nome: string }) {
  const navegar = useNavigate();
  const trocar = useTrocarSenha();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');

  const naoConfere = confirmacao.length > 0 && senhaNova !== confirmacao;
  const pronto = senhaAtual.length >= 8 && senhaNova.length >= 8 && !naoConfere;

  function submeter(evento: FormEvent): void {
    evento.preventDefault();
    if (!pronto) return;
    trocar.mutate(
      { senhaAtual, senhaNova },
      {
        onSuccess: () => {
          setSenhaAtual('');
          setSenhaNova('');
          setConfirmacao('');
          void navegar({ to: '/' });
        },
      },
    );
  }

  return (
    <Moldura titulo="Defina uma nova senha" subtitulo={`${nome}, sua senha atual é provisória.`}>
      <form onSubmit={submeter} className="flex flex-col gap-4">
        <Campo rotulo="Senha atual" obrigatorio>
          {(props) => (
            <Input
              {...props}
              autoFocus
              type="password"
              autoComplete="current-password"
              value={senhaAtual}
              onChange={(e) => setSenhaAtual(e.target.value)}
            />
          )}
        </Campo>

        <Campo rotulo="Nova senha" obrigatorio dica="Entre 8 e 72 caracteres.">
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={senhaNova}
              onChange={(e) => setSenhaNova(e.target.value)}
            />
          )}
        </Campo>

        <Campo rotulo="Repita a nova senha" obrigatorio erro={naoConfere ? 'As senhas não conferem.' : undefined}>
          {(props) => (
            <Input
              {...props}
              type="password"
              autoComplete="new-password"
              value={confirmacao}
              onChange={(e) => setConfirmacao(e.target.value)}
            />
          )}
        </Campo>

        {trocar.error && <ErroDeLogin erro={trocar.error} />}

        <Botao
          type="submit"
          carregando={trocar.isPending}
          textoCarregando="Salvando…"
          disabled={!pronto}
          className="mt-1 w-full"
        >
          Salvar e entrar
        </Botao>
      </form>
    </Moldura>
  );
}

function Moldura({
  children,
  titulo = 'Sistema TCM',
  subtitulo = 'Entre com seu usuário e senha.',
}: {
  children: React.ReactNode;
  titulo?: string;
  subtitulo?: string;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-4 py-10">
      <div className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-sm ring-1 ring-slate-200">
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src="/ico.png" alt="" aria-hidden className="h-12 w-12 rounded-xl object-contain" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">{titulo}</h1>
            <p className="mt-1 text-sm text-slate-500">{subtitulo}</p>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Erro de login tem uma diferença que importa para quem está no balcão:
 * "senha errada" se resolve tentando de novo; conta bloqueada, não.
 */
function ErroDeLogin({ erro }: { erro: unknown }) {
  if (!(erro instanceof ApiError)) {
    return <Faixa tipo="erro">Não foi possível entrar. Tente novamente.</Faixa>;
  }
  if (erro.ehIndisponibilidade) {
    return (
      <Faixa tipo="erro">
        <strong className="block">Servidor indisponível.</strong>
        Não foi possível falar com o banco de dados. Chame o suporte técnico.
      </Faixa>
    );
  }
  return (
    <Faixa tipo={erro.code === 'CONTA_BLOQUEADA' ? 'aviso' : 'erro'}>{erro.message}</Faixa>
  );
}
